/* eslint-disable import/no-extraneous-dependencies */
import React, {useState, useEffect} from 'react';
import {Dialog, DialogContent, Typography, DialogActions, Button, Box} from '@mui/material';

import {SessionEvent, ConCurrentContentProps} from './Types';
import {useStyles} from './Timeout.styles';

// Add type guard for SessionEvent at the top of the file
function hasSessionEventShape(obj: unknown): obj is SessionEvent {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'type' in obj &&
    typeof (obj as Record<string, unknown>)['type'] === 'string' &&
    'message' in obj &&
    typeof (obj as Record<string, unknown>)['message'] === 'string' &&
    'timestamp' in obj &&
    typeof (obj as Record<string, unknown>)['timestamp'] === 'number'
  );
}

export const ConCurrentSessionMonitor = ({content}: {content: ConCurrentContentProps}) => {
  const classes = useStyles();
  const [sessionTerminatedAlert, setSessionTerminatedAlert] = useState<{
    show: boolean;
    message: string;
  }>({
    show: false,
    message: ''
  });

  // Session monitoring effect
  useEffect(() => {
    let eventSource: EventSource | null = null;
    try {
      eventSource = new EventSource(`/api/session-monitor`, {
        withCredentials: true
      });

      eventSource.addEventListener('session-event', (event: MessageEvent) => {
        try {
          const raw: unknown = event.data;
          if (typeof raw === 'string') {
            const data: unknown = JSON.parse(raw);
            if (typeof data === 'object' && data !== null && hasSessionEventShape(data)) {
              const sessionEvent = data;
              if (sessionEvent.type === 'session_terminated') {
                // Show browser notification if available
                if ('Notification' in window && Notification?.permission === 'granted') {
                  // eslint-disable-next-line no-new
                  new Notification('Session Alert', {
                    body: sessionEvent.message || content.sessionTerminatedMsg,
                    icon: '/favicon.ico',
                    tag: 'session-alert'
                  });
                }
                // Show in-app notification
                setSessionTerminatedAlert({
                  show: true,
                  message: sessionEvent.message || content.sessionTerminatedMsg
                });
                // Clear local session data
                sessionStorage.removeItem('session');
                sessionStorage.removeItem('loginType');
                sessionStorage.removeItem('domain');
                sessionStorage.removeItem('Session-id');
              }
            }
          }
        } catch {
          // Handle error
        }
      });

      eventSource.onerror = () => {
        // Handle error
      };
    } catch {
      // Handle error
    }

    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification?.permission === 'default') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      Notification.requestPermission();
    }
  }, []);

  const handleSessionTerminatedDialogClose = () => {
    setSessionTerminatedAlert({show: false, message: ''});
    // Redirect to login after closing the dialog
    document.body.classList.remove('modalOpen');
    sessionStorage.removeItem('session');
    sessionStorage.removeItem('loginType');
    sessionStorage.removeItem('domain');
    sessionStorage.removeItem('Session-id');
    window.location.href = `/api/auth/logout`;
  };

  return (
    <Dialog
      open={sessionTerminatedAlert.show}
      onClose={handleSessionTerminatedDialogClose}
      PaperProps={{style: {borderRadius: '24px', padding: '32px', width: '500px'}}}
      aria-labelledby="session-terminated-dialog"
    >
      <Box className={classes.modalHead}>
        <Typography variant="h3" color="primary" className={classes.modalNotification}>
          <span className="serviceStatus"> {content.sessionTerminatedLabel}</span>
        </Typography>
      </Box>
      <DialogContent style={{padding: 0}}>
        <Typography variant="body1">{sessionTerminatedAlert.message}</Typography>
      </DialogContent>
      <DialogActions className={classes.modalFooter}>
        <Button onClick={handleSessionTerminatedDialogClose} variant="contained" color="primary" sx={{minWidth: 120}}>
          {content.okLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
